import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, SearchIcon, TextInput } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { FieldLabel, PeriodDateRangeFields, formatReportAmount } from './shared/reportUtils';
import { buildReportQuery, useAccountingReportFilters } from './shared/useAccountingReportFilters';
import { accountingReturnState } from '../shared/navigation';

type Row = {
  accountId: number;
  accountCode: string;
  accountName: string;
  combLevel: string;
  id: number;
  documentId: number | null;
  date: string | null;
  docCode: string | null;
  narrative: string;
  companyCode: string;
  debitLocal: number;
  creditLocal: number;
  localBalance: number;
};

type Response = {
  rows: Row[];
  recordCount: number;
  accountsLimited?: boolean;
  totalPostingAccounts?: number;
  accountsIncluded?: number;
  accountLimit?: number;
};

export const GeneralLedger: React.FC = () => {
  const { colors } = useTheme();
  const location = useLocation();
  const f = useAccountingReportFilters();
  const [combLevelFrom, setCombLevelFrom] = React.useState('');
  const [combLevelTo, setCombLevelTo] = React.useState('');

  const reportUrl =
    f.run
      ? buildReportQuery('/accounting/reports/general-ledger', {
          dateFrom: f.dateFrom,
          dateTo: f.dateTo,
          companyIds: f.companyIdsParam || undefined,
          combLevelFrom: combLevelFrom || undefined,
          combLevelTo: combLevelTo || undefined,
        })
      : null;

  const { data, isLoading } = useSWR<Response>(reportUrl, fetcher);

  const hasAccountScope = !!(combLevelFrom.trim() || combLevelTo.trim());

  const returnState = useMemo(
    () => accountingReturnState(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const columns = useMemo<Column<Row>[]>(
    () => [
      { key: 'combLevel', header: 'Account', render: (_v, row) => row.accountName },
      { key: 'date', header: 'Date', render: (v) => (v ? formatDateGlobal(v) : '—') },
      {
        key: 'docCode',
        header: 'Doc #',
        render: (v, row) =>
          row.documentId && v ? (
            <Link
              to={`/accounting/journals/${row.documentId}/edit`}
              state={returnState}
              className="text-indigo-600 hover:underline"
            >
              {v}
            </Link>
          ) : (
            v ?? '—'
          ),
      },
      { key: 'companyCode', header: 'Co.' },
      { key: 'narrative', header: 'Details' },
      { key: 'debitLocal', header: 'Debit', align: 'right', render: formatReportAmount },
      { key: 'creditLocal', header: 'Credit', align: 'right', render: formatReportAmount },
      { key: 'localBalance', header: 'Balance', align: 'right', render: (v: number) => formatReportAmount(v) },
      {
        key: 'kartela',
        header: '',
        width: '3rem',
        render: (_v, row) => (
          <Link
            to={`/accounting/reports/ledger-kartela/${row.accountId}?dateFrom=${f.dateFrom}&dateTo=${f.dateTo}${f.companyIdsParam ? `&companyIds=${f.companyIdsParam}` : ''}`}
            className="text-xs text-indigo-600 hover:underline"
          >
            Card
          </Link>
        ),
      },
    ],
    [f.dateFrom, f.dateTo, f.companyIdsParam, returnState],
  );

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <PeriodDateRangeFields
          periodId={f.periodId}
          periodOptions={f.periodOptions}
          onPeriodChange={(value) => {
            f.setPeriodId(value);
            f.setRun(false);
          }}
          dateFrom={f.dateFrom}
          dateTo={f.dateTo}
          onDateFromChange={(value) => {
            f.setPeriodId('');
            f.setDateFrom(value);
            f.setRun(false);
          }}
          onDateToChange={(value) => {
            f.setPeriodId('');
            f.setDateTo(value);
            f.setRun(false);
          }}
        />
        <div>
          <FieldLabel>Comb level from</FieldLabel>
          <TextInput value={combLevelFrom} onChange={(e) => { setCombLevelFrom(e.target.value); f.setRun(false); }} placeholder="e.g. 00.00" />
        </div>
        <div>
          <FieldLabel>Comb level to</FieldLabel>
          <TextInput value={combLevelTo} onChange={(e) => { setCombLevelTo(e.target.value); f.setRun(false); }} placeholder="e.g. 99.99" />
        </div>
      </div>
      <div className={cn('flex items-center gap-2 pt-3 border-t', colors.border)}>
        <Badge variant="secondary" className="h-10 px-3 flex items-center gap-1.5">
          <span className={cn('h-1.5 w-1.5 rounded-full', f.allCompanies ? 'bg-emerald-500' : 'bg-amber-500')} />
          {f.companyScopeLabel}
        </Badge>
        <Button variant="default" onClick={() => f.setRun(true)} disabled={!f.canSearch} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>
      </div>
      {!hasAccountScope && !f.run && (
        <p
          className={cn(
            'text-sm rounded-lg border px-3 py-2',
            'border-amber-200 bg-amber-50 text-amber-900',
            'dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100',
          )}
        >
          Without comb level filters, only the first 50 posting accounts (by comb level) are included. Use{' '}
          <strong>Comb level from/to</strong> to narrow the range.
        </p>
      )}
    </Card>
  );

  const rows = data?.rows ?? [];
  const showLimitWarning = f.run && !isLoading && data?.accountsLimited;

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={f.run}
      loading={isLoading}
      rows={rows}
      columns={columns}
      entityName="General Ledger"
      exportFileBase="general-ledger"
      exportDateTo={f.dateTo}
      statusBadge={
        f.run ? (
          <Badge variant="info" className="font-normal">
            {formatDateGlobal(f.dateFrom)} – {formatDateGlobal(f.dateTo)}
          </Badge>
        ) : undefined
      }
      statusExtra={
        showLimitWarning ? (
          <Badge variant="warning" className="font-normal max-w-md whitespace-normal text-left">
            {data?.accountsIncluded ?? 0} of {data?.totalPostingAccounts ?? 0} accounts — add comb level filters
          </Badge>
        ) : undefined
      }
      recordCount={data?.recordCount ?? rows.length}
      readyMessage="Choose period and optional account range, then search."
    >
      {showLimitWarning && (
        <div
          className={cn(
            'mx-4 mt-3 rounded-lg border px-4 py-3 text-sm shrink-0',
            'border-amber-200 bg-amber-50 text-amber-900',
            'dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100',
          )}
        >
          Showing <strong>{data?.accountsIncluded ?? 0}</strong> of{' '}
          <strong>{data?.totalPostingAccounts ?? 0}</strong> posting accounts (limit{' '}
          {data?.accountLimit ?? 50}). Add <strong>Comb level from/to</strong> filters and search again to include
          other accounts.
        </div>
      )}
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={rows}
        columns={columns}
        keyExtractor={(r) => String(r.id)}
        stickyHeader
        emptyMessage="No transactions in range."
      />
    </AccountingReportShell>
  );
};
